import type { Fact } from '../../../domain/types.ts';

const V = '1.0';
const AB = '2021-08-01';
const UNBEGRENZT = null;

/**
 * Schutzmaßnahmen, Grenzwerte und Kennlinien.
 *
 * WICHTIG: Jeder Wert trägt einen Prüfstatus. `offen` heißt: der Wert ist
 * eingetragen und mit einer Quellenangabe hinterlegt, aber noch nicht am
 * Original abgeglichen. Offene Fakten werden im UI und im Prüfbericht
 * gekennzeichnet und nie als gesichert dargestellt.
 */
export const FAKTEN_SCHUTZ: Fact[] = [
  // --- Nennspannungen (Quelle: System 230/400 V nach Übergangsregeln) -------
  {
    id: 'u0-230',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Bemessungsspannung Leiter gegen Erde U0 (TN-System)',
    wert: 230,
    einheit: 'V',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Historisch 220 V, seit der Harmonisierung 230 V Systemspannung, ' +
      'tatsächliche Werte 230/400 V ±10 %.',
    verification: 'geprueft',
    tags: ['spannung', 'tn-system'],
  },
  {
    id: 'u0-400',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Bemessungsspannung Leiter gegen Leiter U (Drehstrom)',
    wert: 400,
    einheit: 'V',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'Aus Systemspannung 400 V, tatsächlicher Bereich 360…440 V.',
    verification: 'geprueft',
    tags: ['spannung', 'drehstrom'],
  },
  {
    id: 'u0-24',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Kleinspannung (SELV/PELV) U0',
    wert: 24,
    einheit: 'V',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'Grenze der Kleinspannung nach System 230/400 V.',
    verification: 'geprueft',
    tags: ['selv', 'kleinspannung'],
  },
  {
    id: 'u0-50',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Grenzwert U0 für besondere Stromkreise im Freien',
    wert: 50,
    einheit: 'V',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Wird in Übungen häufig mit 100 Ω und IΔn = 500 mA (Baustromverteiler) ' +
      'kombiniert – Wert und Kombination getrennt prüfen.',
    verification: 'offen',
    tags: ['abschaltbedingung'],
  },

  // --- Fehlerstromschutzeinrichtungen --------------------------------------
  {
    id: 'idn-personenschutz',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Bemessungsfehlerstrom IΔn für zusätzlichen Personenschutz',
    wert: 0.03,
    einheit: 'A',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung:
      '30 mA für Steckdosen und Stromkreise, die durch Users Bereiche besonderer ' +
      'Art führen (Badezimmer, Außenbereich, Baustelle u. a.).',
    verification: 'geprueft',
    tags: ['rcd', 'personenschutz', 'abschaltzeit-0-3s'],
  },
  {
    id: 'idn-feuchteraum',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Bemessungsfehlerstrom IΔn für Feuchträume ohne Personenschutz',
    wert: 0.3,
    einheit: 'A',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'Typischer Unterrichtswert; Anforderungen je Raum prüfen.',
    verification: 'geprueft',
    tags: ['rcd', 'feuchteraum', 'abschaltzeit-0-3s'],
  },
  {
    id: 'idn-baustelle-30ma',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'IΔn 30 mA für Baustellenverbrauchergeräte',
    wert: 0.03,
    einheit: 'A',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'Baustellenanlagen nach VDE 0100-712.',
    verification: 'geprueft',
    tags: ['rcd', 'baustelle'],
  },
  {
    id: 'abschaltzeit-0-3s',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Zulässige Auslösezeit bei IΔn = 300 mA',
    wert: 0.3,
    einheit: 's',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Bei IΔn = 30 mA sind höchstens 300 ms an der Verbraucherstelle zulässig; ' +
      'bei 300 mA gilt für den Anlagenteil bis 300 ms.',
    verification: 'geprueft',
    tags: ['rcd', 'abschaltzeit'],
  },
  {
    id: 'rcd-typ-a',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Fehlerstromschutzschalter Typ A – erfasst Wechselstromfehler',
    wert: 0.3,
    einheit: 'A',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Typ A erfasst sinusförmige Wechselstromfehler und pulsförmige Gleichanteile, ' +
      'z. B. durch dimmbare Leuchten oder Elektronik.',
    verification: 'geprueft',
    tags: ['rcd', 'typ-a'],
  },

  // --- Leitungenschutzschalter-Kennlinien ----------------------------------
  {
    id: 'ls-kennlinie-b-magnetisch',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Leitungenschutzschalter Kennlinie B – magnetisches Auslösen',
    wert: 5,
    einheit: '× In',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'B: thermisch 1,13–1,45 × In, magnetisch 3–5 × In.',
    verification: 'geprueft',
    tags: ['ls', 'kennlinie-b'],
  },
  {
    id: 'ls-kennlinie-c-magnetisch',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Leitungenschutzschalter Kennlinie C – magnetisches Auslösen',
    wert: 10,
    einheit: '× In',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'C: thermisch 1,13–1,45 × In, magnetisch 5–10 × In.',
    verification: 'geprueft',
    tags: ['ls', 'kennlinie-c'],
  },
  {
    id: 'ls-kennlinie-d-magnetisch',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Leitungenschutzschalter Kennlinie D – magnetisches Auslösen',
    wert: 20,
    einheit: '× In',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'D: thermisch 1,13–1,45 × In, magnetisch 10–20 × In.',
    verification: 'geprueft',
    tags: ['ls', 'kennlinie-d'],
  },
  {
    id: 'ls-kennlinie-b-magnetisch-min',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Leitungenschutzschalter Kennlinie B – magnetisches Auslösen, untere Grenze',
    wert: 3,
    einheit: '× In',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Für die Abschaltbedingung R_L ≤ U₀/I_a ist der kleinste Auslösestrom ' +
      'maßgeblich, also die untere Grenze des magnetischen Bereichs.',
    verification: 'geprueft',
    tags: ['ls', 'kennlinie-b', 'ausloesestrom'],
  },
  {
    id: 'ls-kennlinie-c-magnetisch-min',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Leitungenschutzschalter Kennlinie C – magnetisches Auslösen, untere Grenze',
    wert: 5,
    einheit: '× In',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'Maßgeblich für R_L ≤ U₀/I_a.',
    verification: 'geprueft',
    tags: ['ls', 'kennlinie-c', 'ausloesestrom'],
  },
  {
    id: 'ls-kennlinie-d-magnetisch-min',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Leitungenschutzschalter Kennlinie D – magnetisches Auslösen, untere Grenze',
    wert: 10,
    einheit: '× In',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'Maßgeblich für R_L ≤ U₀/I_a.',
    verification: 'geprueft',
    tags: ['ls', 'kennlinie-d', 'ausloesestrom'],
  },
  {
    id: 'ls-typ-faktor',
    version: V,
    kategorie: 'Schutz',
    bezeichnung: 'Thermischer Auslösebereich Leitungenschutzschalter (untere Grenze)',
    wert: 1.13,
    einheit: '× In',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung: 'Zum Vergleich: 1,45 × In ist die obere thermische Auslösegrenze.',
    verification: 'geprueft',
    tags: ['ls', 'thermisch'],
  },

  // --- Wiederholungsprüfung -------------------------------------------------
  {
    id: 'riso-grenzwert',
    version: V,
    kategorie: 'Prüfung',
    bezeichnung: 'Isolationswiderstand – Mindestwert bei Prüfspannung 500 V DC',
    wert: 1,
    einheit: 'MΩ',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm', 'ZVEH'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Vor Ort wird regelmäßig mit 0,5 MΩ als Mindestwert gerechnet. Beide Werte ' +
      'sind in Übungen verbreitet – Normstelle im Original prüfen, bevor dieser ' +
      'Wert in einer Prüfungsaufgabe verwendet wird.',
    verification: 'offen',
    tags: ['wiederholungspruefung', 'isolation'],
  },
  {
    id: 're-grenzwert',
    version: V,
    kategorie: 'Prüfung',
    bezeichnung: 'Erdungswiderstand R_E – Grenzwert bei 300 mA Abschaltstrom',
    wert: 166.7,
    einheit: 'Ω',
    quelleId: 'vde0100',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Rechnerisch U0 / IΔn = 50 V / 0,3 A. 100 Ω gelten bei 500 mA. ' +
      'Häufig als "R_E ≤ 100 Ω" zitiert – Wert immer aus U0 und IΔn herleiten.',
    verification: 'geprueft',
    tags: ['wiederholungspruefung', 'erdung'],
  },

  // --- Geräteprüfung nach DIN VDE 0701-0702 --------------------------------
  {
    id: 'pe-widerstand-geraet',
    version: V,
    kategorie: 'Prüfung',
    bezeichnung: 'Schutzleiterwiderstand am Betriebsmittel (SK I)',
    wert: 0.3,
    einheit: 'Ω',
    quelleId: 'vde0701',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm', 'ZVEH'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Grenzwert für Anschlussleitungen bis 5 m bei Bemessungsstrom bis 16 A. ' +
      'Für jede weitere 7,5 m Leitungslänge 0,1 Ω mehr, höchstens 1 Ω. ' +
      'Gemessen wird zwischen dem Schutzleiter des Netzsteckers und berührbaren ' +
      'leitfähigen Teilen.',
    verification: 'geprueft',
    tags: ['geraetepruefung', 'schutzleiter', 'wiederholungspruefung'],
  },
  {
    id: 'riso-geraet-sk1',
    version: V,
    kategorie: 'Prüfung',
    bezeichnung: 'Isolationswiderstand am Betriebsmittel der Schutzklasse I',
    wert: 1,
    einheit: 'MΩ',
    quelleId: 'vde0701',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm', 'ZVEH'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Mindestens 1 MΩ bei Geräten der Schutzklasse I. Geräte mit Heizwicklung ' +
      'dürfen bis auf 0,3 MΩ absinken. Für Schutzklasse II gilt 2 MΩ.',
    verification: 'geprueft',
    tags: ['geraetepruefung', 'isolation', 'wiederholungspruefung'],
  },
  {
    id: 'schutzleiterstrom-geraet',
    version: V,
    kategorie: 'Prüfung',
    bezeichnung: 'Schutzleiterstrom (Ableitstrom) am Betriebsmittel der Schutzklasse I',
    wert: 3.5,
    einheit: 'mA',
    quelleId: 'vde0701',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm', 'ZVEH'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Höchstens 3,5 mA. Bei Geräten mit Heizelementen über 3,5 kW gilt ' +
      '1 mA je kW Heizleistung, höchstens 10 mA. Der Ersatzableitstrom wird als ' +
      'Ersatzverfahren an spannungsfreiem Gerät gemessen und mit demselben ' +
      'Grenzwert bewertet.',
    verification: 'geprueft',
    tags: ['geraetepruefung', 'ableitstrom', 'wiederholungspruefung'],
  },
  {
    id: 'beruehrungsstrom-geraet',
    version: V,
    kategorie: 'Prüfung',
    bezeichnung: 'Berührungsstrom an berührbaren leitfähigen Teilen',
    wert: 0.5,
    einheit: 'mA',
    quelleId: 'vde0701',
    gueltigAb: AB,
    gueltigBis: UNBEGRENZT,
    ersetztDurch: null,
    pruefungsrelevanz: ['Norm', 'ZVEH'],
    region: null,
    jahrgang: null,
    bemerkung:
      'Höchstens 0,5 mA an berührbaren leitfähigen Teilen, die nicht mit dem ' +
      'Schutzleiter verbunden sind. Gilt ebenso für Betriebsmittel der ' +
      'Schutzklasse II.',
    verification: 'geprueft',
    tags: ['geraetepruefung', 'beruehrungsstrom', 'wiederholungspruefung'],
  },
];
